import { createClient, SupabaseClient } from '@supabase/supabase-js'

// Supabase client instance
let supabaseInstance: SupabaseClient | null = null

// Get Supabase URL from environment variables with .env fallback
const getSupabaseUrl = () => {
  try {
    if (
      typeof import.meta !== 'undefined' &&
      import.meta.env?.VITE_SUPABASE_URL
    ) {
      return import.meta.env.VITE_SUPABASE_URL
    }
  } catch (e) {}
  // Fallback: read from .env at build time
  return 'https://sanpzousriwcnatnenpp.supabase.co'
}

// Get Supabase anon key from environment variables with .env fallback
const getSupabaseAnonKey = () => {
  try {
    if (
      typeof import.meta !== 'undefined' &&
      import.meta.env?.VITE_SUPABASE_ANON_KEY
    ) {
      return import.meta.env.VITE_SUPABASE_ANON_KEY
    }
  } catch (e) {}
  // Fallback: read from .env at build time
  return 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InNhbnB6b3Vzcml3Y25hdG5lbnBwIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Njg5MTk3NzksImV4cCI6MjA4NDQ5NTc3OX0.G6lHZGDu5QziIWl12jXx8DvrCb2ogGF0eA2F7XzOUL4'
}

// Initialize Supabase client
const initializeSupabase = () => {
  const url = getSupabaseUrl()
  const key = getSupabaseAnonKey()

  if (url && key) {
    supabaseInstance = createClient(url, key, {
      global: {
        headers: {},
        fetch: fetchWithRetry,
      },
    })
    return supabaseInstance
  }

  supabaseInstance = null
  return null
}

// Retry wrapper for fetch - handles transient errors (520, 502, 503, 504)
const RETRYABLE_STATUS_CODES = [520, 502, 503, 504, 408, 429]
const MAX_RETRIES = 3
const BASE_DELAY_MS = 1000

async function fetchWithRetry(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  let lastError: Error | null = null

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      const response = await fetch(input, init)

      // If response is OK or a non-retryable error, return immediately
      if (!RETRYABLE_STATUS_CODES.includes(response.status)) {
        return response
      }

      // Retryable status code - wait and retry
      lastError = new Error(
        `Request failed with status code ${response.status}`,
      )

      if (attempt < MAX_RETRIES) {
        const delay = BASE_DELAY_MS * Math.pow(2, attempt) + Math.random() * 500
        console.warn(
          `[Supabase] Retryable error ${response.status}, retrying in ${Math.round(delay)}ms (attempt ${attempt + 1}/${MAX_RETRIES})`,
        )
        await new Promise((resolve) => setTimeout(resolve, delay))
      } else {
        // Last attempt failed - return the response as-is so Supabase client can handle it
        return response
      }
    } catch (networkError: any) {
      // Network error (offline, DNS failure, etc.)
      lastError = networkError

      if (attempt < MAX_RETRIES) {
        const delay = BASE_DELAY_MS * Math.pow(2, attempt) + Math.random() * 500
        console.warn(
          `[Supabase] Network error, retrying in ${Math.round(delay)}ms (attempt ${attempt + 1}/${MAX_RETRIES}):`,
          networkError.message,
        )
        await new Promise((resolve) => setTimeout(resolve, delay))
      }
    }
  }

  // All retries exhausted
  throw lastError || new Error('Request failed after all retries')
}

// Get current Supabase client (reinitialize if needed)
export const getSupabase = () => {
  if (!supabaseInstance) {
    initializeSupabase()
  }
  return supabaseInstance
}

// Legacy export for compatibility
export const supabase = getSupabase()

// Check if Supabase is configured
export const isSupabaseConfigured = () => {
  const url = getSupabaseUrl()
  const key = getSupabaseAnonKey()
  return Boolean(url && key)
}

// Utility: Generate secure random ID
const generateSecureId = (prefix: string): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return `${prefix}-${crypto.randomUUID()}`
  }
  // Fallback with crypto.getRandomValues
  const arr = new Uint8Array(16)
  crypto.getRandomValues(arr)
  const hex = Array.from(arr)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
  return `${prefix}-${hex}`
}

// Database helpers
export const supabaseClient = {
  // ============================================
  // AUTHENTICATION (SECURE)
  // ============================================

  /**
   * Unified authentication: tries admin first, then employee.
   * Uses server-side RPC with bcrypt - credentials NEVER touch the frontend.
   */
  async authenticateUser(username: string, password: string) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    try {
      const { data, error } = await client.rpc('authenticate_user', {
        p_username: username,
        p_password: password,
      })

      if (error) {
        // If unified function doesn't exist yet, fall back to separate calls
        if (
          error.code === '42883' ||
          error.message?.includes('does not exist')
        ) {
          return this._fallbackAuth(username, password)
        }
        throw new Error('Error de autenticación: ' + error.message)
      }

      if (!data) return null

      return {
        id: data.id,
        name: data.name,
        email: data.email,
        role: data.role as 'admin' | 'employee',
        username: data.username || undefined,
        company_id: data.company_id ? Number(data.company_id) : undefined,
        branch_id: data.branch_id || undefined,
        status: data.status || undefined,
        permissions: data.permissions || undefined,
      }
    } catch (e: any) {
      throw new Error(e.message || 'Error al autenticar')
    }
  },

  /**
   * Fallback: use authenticate_employee RPC if authenticate_user doesn't exist yet.
   * Will be removed once SUPABASE_ADMIN_AUTH.sql is executed.
   */
  async _fallbackAuth(username: string, password: string) {
    return this.authenticateEmployee(username, password)
  },

  /**
   * Authenticate an employee by username and password.
   * Uses server-side RPC function with bcrypt verification.
   * NEVER exposes passwords to the frontend.
   */
  async authenticateEmployee(username: string, password: string) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    try {
      const { data, error } = await client.rpc('authenticate_employee', {
        p_username: username,
        p_password: password,
      })

      if (error) {
        throw new Error('Error de autenticación: ' + error.message)
      }

      if (!data) return null

      // RPC returned employee data (already without password)
      return {
        id: data.id,
        name: data.name,
        email: data.email,
        username: data.username,
        company_id: data.company_id,
        branch_id: data.branch_id,
        status: data.status,
        permissions: data.permissions,
      }
    } catch (e: any) {
      throw new Error(e.message || 'Error al autenticar')
    }
  },

  // ============================================
  // ORDERS
  // ============================================
  async getOrders() {
    const client = getSupabase()
    if (!client) {
      throw new Error('Supabase not configured')
    }

    const { data, error } = await client
      .from('orders')
      .select('*')
      .order('created_at', { ascending: false })

    if (error) {
      throw error
    }

    return data || []
  },

  async createOrder(order: any) {
    const client = getSupabase()
    if (!client) {
      throw new Error('Supabase not configured')
    }

    const { data, error } = await client.from('orders').insert([order]).select()

    if (error) {
      throw error
    }

    return data[0]
  },

  async updateOrder(id: string, updates: any) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const { data, error } = await client
      .from('orders')
      .update(updates)
      .eq('id', id)
      .select()

    if (error) {
      throw error
    }

    return data[0]
  },

  async deleteOrder(id: string) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const { error } = await client.from('orders').delete().eq('id', id)
    if (error) throw error
  },

  // ============================================
  // EMPLOYEES (SECURE - NO PASSWORDS)
  // ============================================

  /**
   * Get all employees WITHOUT password field.
   * Use this for listing/display purposes.
   */
  async getEmployees() {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const { data, error } = await client
      .from('employees')
      .select(
        'id, name, email, username, position, company_id, branch_id, status, permissions, created_at',
      )
      .order('name', { ascending: true })

    if (error) throw error
    return data || []
  },

  async createEmployee(employee: any) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const { data, error } = await client
      .from('employees')
      .insert([employee])
      .select(
        'id, name, email, username, position, company_id, branch_id, status, permissions, created_at',
      )

    if (error) throw error
    return data[0]
  },

  async updateEmployee(id: string, updates: any) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const { data, error } = await client
      .from('employees')
      .update(updates)
      .eq('id', id)
      .select(
        'id, name, email, username, position, company_id, branch_id, status, permissions, created_at',
      )

    if (error) throw error
    return data[0]
  },

  async deleteEmployee(id: string) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const { error } = await client.from('employees').delete().eq('id', id)
    if (error) throw error
  },

  // ============================================
  // COMPANIES
  // ============================================
  async getCompanies() {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const { data, error } = await client
      .from('companies')
      .select('*')
      .order('name', { ascending: true })

    if (error) {
      throw error
    }

    // Transform snake_case to camelCase for frontend compatibility
    const companies = (data || []).map((c: any) => ({
      id: c.id,
      name: c.name,
      rnc: c.rnc,
      logo: c.logo,
      branches: c.branches || [],
      employees: c.employees || 0,
      taxExempt: c.tax_exempt,
      subsidy: c.subsidy,
      subsidyEnabled: c.subsidy_enabled || false,
      orderCutoffTime: c.order_cutoff_time,
      deliveryStartTime: c.delivery_start_time,
      deliveryEndTime: c.delivery_end_time,
      contactName: c.contact_name,
      contactEmail: c.contact_email,
      contactPhone: c.contact_phone,
      companyPrices: c.company_prices || {},
      assignedCategories: c.assigned_categories || [],
      createdAt: c.created_at,
    }))

    return companies
  },

  async createCompany(company: any) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    // Transform camelCase to snake_case for Supabase
    const dbCompany = {
      name: company.name,
      rnc: company.rnc,
      logo: company.logo || null,
      branches: company.branches || [],
      employees: company.employees || 0,
      tax_exempt: company.taxExempt || false,
      subsidy: company.subsidy || 'RD$ 0',
      order_cutoff_time: company.orderCutoffTime || '23:00',
      delivery_start_time: company.deliveryStartTime || '12:00',
      delivery_end_time: company.deliveryEndTime || '14:00',
      contact_name: company.contactName || null,
      contact_email: company.contactEmail || null,
      contact_phone: company.contactPhone || null,
      company_prices: company.companyPrices || {},
      assigned_categories: company.assignedCategories || [],
    }

    const { data, error } = await client
      .from('companies')
      .insert([dbCompany])
      .select()

    if (error) {
      throw error
    }

    return data[0]
  },

  async updateCompany(id: number, updates: any) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    // Transform camelCase to snake_case for Supabase
    const dbUpdates: any = {}
    if (updates.name !== undefined) dbUpdates.name = updates.name
    if (updates.rnc !== undefined) dbUpdates.rnc = updates.rnc
    if (updates.logo !== undefined) dbUpdates.logo = updates.logo
    if (updates.branches !== undefined) dbUpdates.branches = updates.branches
    if (updates.taxExempt !== undefined)
      dbUpdates.tax_exempt = updates.taxExempt
    if (updates.subsidy !== undefined) dbUpdates.subsidy = updates.subsidy
    if (updates.subsidyEnabled !== undefined)
      dbUpdates.subsidy_enabled = updates.subsidyEnabled
    if (updates.orderCutoffTime !== undefined)
      dbUpdates.order_cutoff_time = updates.orderCutoffTime
    if (updates.deliveryStartTime !== undefined)
      dbUpdates.delivery_start_time = updates.deliveryStartTime
    if (updates.deliveryEndTime !== undefined)
      dbUpdates.delivery_end_time = updates.deliveryEndTime
    if (updates.contactName !== undefined)
      dbUpdates.contact_name = updates.contactName
    if (updates.contactEmail !== undefined)
      dbUpdates.contact_email = updates.contactEmail
    if (updates.contactPhone !== undefined)
      dbUpdates.contact_phone = updates.contactPhone
    if (updates.companyPrices !== undefined)
      dbUpdates.company_prices = updates.companyPrices
    if (updates.assignedCategories !== undefined)
      dbUpdates.assigned_categories = updates.assignedCategories

    const { data, error } = await client
      .from('companies')
      .update(dbUpdates)
      .eq('id', id)
      .select()

    if (error) {
      throw error
    }

    return data[0]
  },

  async deleteCompany(id: number) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const { error } = await client.from('companies').delete().eq('id', id)
    if (error) throw error
  },

  // ============================================
  // MENU ITEMS
  // ============================================
  async getMenuItems() {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const { data, error } = await client
      .from('menu_items')
      .select('*')
      .order('name', { ascending: true })

    if (error) {
      throw error
    }

    // Transform snake_case to camelCase
    const items = (data || []).map((item: any) => ({
      id: item.id,
      name: item.name,
      description: item.description,
      category: item.category,
      price: parseFloat(item.price),
      image: item.image,
      catalogType: item.catalog_type,
      available: item.available,
      createdAt: item.created_at,
    }))

    return items
  },

  async createMenuItem(item: any) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const dbItem = {
      id: item.id || `menu-${Date.now()}`,
      name: item.name,
      description: item.description,
      category: item.category,
      price: item.price,
      image: item.image || null,
      catalog_type: item.catalogType || 'Almuerzo',
      available: item.available !== false,
    }

    const { data, error } = await client
      .from('menu_items')
      .insert([dbItem])
      .select()

    if (error) {
      throw error
    }

    return data[0]
  },

  async updateMenuItem(id: string, updates: any) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const dbUpdates: any = {}
    if (updates.name !== undefined) dbUpdates.name = updates.name
    if (updates.description !== undefined)
      dbUpdates.description = updates.description
    if (updates.category !== undefined) dbUpdates.category = updates.category
    if (updates.price !== undefined) dbUpdates.price = updates.price
    if (updates.image !== undefined) dbUpdates.image = updates.image
    if (updates.catalogType !== undefined)
      dbUpdates.catalog_type = updates.catalogType
    if (updates.available !== undefined) dbUpdates.available = updates.available

    const { data, error } = await client
      .from('menu_items')
      .update(dbUpdates)
      .eq('id', id)
      .select()

    if (error) {
      throw error
    }

    return data[0]
  },

  async deleteMenuItem(id: string) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const { error } = await client.from('menu_items').delete().eq('id', id)
    if (error) throw error
  },

  // ============================================
  // MENU CATEGORIES
  // ============================================
  async getMenuCategories() {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const { data, error } = await client
      .from('menu_categories')
      .select('*')
      .order('name', { ascending: true })

    if (error) {
      throw error
    }

    return (data || []).map((c: any) => c.name)
  },

  async createMenuCategory(name: string) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const { data, error } = await client
      .from('menu_categories')
      .insert([{ name }])
      .select()

    if (error) {
      throw error
    }

    return data[0]
  },

  async deleteMenuCategory(name: string) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const { error } = await client
      .from('menu_categories')
      .delete()
      .eq('name', name)

    if (error) throw error
  },

  // ============================================
  // MENU ITEM SECTIONS
  // ============================================
  async getMenuItemSections(menuItemId: string) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const { data, error } = await client
      .from('menu_item_sections')
      .select('*, menu_section_options(*)')
      .eq('menu_item_id', menuItemId)
      .order('display_order', { ascending: true })

    if (error) {
      throw error
    }

    // Transform to frontend format
    const sections = (data || []).map((section: any) => ({
      id: section.id,
      menuItemId: section.menu_item_id,
      name: section.name,
      description: section.description,
      required: section.required,
      minSelections: section.min_selections,
      maxSelections: section.max_selections,
      displayOrder: section.display_order,
      options: (section.menu_section_options || [])
        .map((opt: any) => ({
          id: opt.id,
          sectionId: opt.section_id,
          name: opt.name,
          priceModifier: parseFloat(opt.price_modifier || 0),
          available: opt.available,
          displayOrder: opt.display_order,
        }))
        .sort((a: any, b: any) => a.displayOrder - b.displayOrder),
    }))

    return sections
  },

  async createMenuSection(section: any) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const dbSection = {
      menu_item_id: section.menuItemId,
      name: section.name,
      description: section.description || null,
      required: section.required || false,
      min_selections: section.minSelections || 0,
      max_selections: section.maxSelections || 1,
      display_order: section.displayOrder || 0,
    }

    const { data, error } = await client
      .from('menu_item_sections')
      .insert([dbSection])
      .select()

    if (error) {
      throw error
    }

    return data[0]
  },

  async updateMenuSection(id: string, updates: any) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const dbUpdates: any = {}
    if (updates.name !== undefined) dbUpdates.name = updates.name
    if (updates.description !== undefined)
      dbUpdates.description = updates.description
    if (updates.required !== undefined) dbUpdates.required = updates.required
    if (updates.minSelections !== undefined)
      dbUpdates.min_selections = updates.minSelections
    if (updates.maxSelections !== undefined)
      dbUpdates.max_selections = updates.maxSelections
    if (updates.displayOrder !== undefined)
      dbUpdates.display_order = updates.displayOrder

    const { data, error } = await client
      .from('menu_item_sections')
      .update(dbUpdates)
      .eq('id', id)
      .select()

    if (error) throw error
    return data[0]
  },

  async deleteMenuSection(id: string) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const { error } = await client
      .from('menu_item_sections')
      .delete()
      .eq('id', id)

    if (error) throw error
  },

  // ============================================
  // MENU SECTION OPTIONS
  // ============================================
  async createSectionOption(option: any) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const dbOption = {
      section_id: option.sectionId,
      name: option.name,
      price_modifier: option.priceModifier || 0,
      available: option.available !== false,
      display_order: option.displayOrder || 0,
    }

    const { data, error } = await client
      .from('menu_section_options')
      .insert([dbOption])
      .select()

    if (error) {
      throw error
    }

    return data[0]
  },

  async updateSectionOption(id: string, updates: any) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const dbUpdates: any = {}
    if (updates.name !== undefined) dbUpdates.name = updates.name
    if (updates.priceModifier !== undefined)
      dbUpdates.price_modifier = updates.priceModifier
    if (updates.available !== undefined) dbUpdates.available = updates.available
    if (updates.displayOrder !== undefined)
      dbUpdates.display_order = updates.displayOrder

    const { data, error } = await client
      .from('menu_section_options')
      .update(dbUpdates)
      .eq('id', id)
      .select()

    if (error) throw error
    return data[0]
  },

  async deleteSectionOption(id: string) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const { error } = await client
      .from('menu_section_options')
      .delete()
      .eq('id', id)

    if (error) throw error
  },

  // ============================================
  // MENU SECTION TEMPLATES (PLANTILLAS REUTILIZABLES)
  // ============================================

  async getSectionTemplates() {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const { data, error } = await client
      .from('menu_section_templates')
      .select('*, menu_template_options(*)')
      .order('name', { ascending: true })

    if (error) {
      if (error.code === '42P01') return [] // Table doesn't exist yet
      throw error
    }

    return (data || []).map((t: any) => ({
      id: t.id,
      name: t.name,
      description: t.description,
      required: t.required,
      minSelections: t.min_selections,
      maxSelections: t.max_selections,
      createdAt: t.created_at,
      options: (t.menu_template_options || [])
        .map((opt: any) => ({
          id: opt.id,
          templateId: opt.template_id,
          name: opt.name,
          priceModifier: parseFloat(opt.price_modifier || 0),
          available: opt.available,
          displayOrder: opt.display_order,
        }))
        .sort((a: any, b: any) => a.displayOrder - b.displayOrder),
    }))
  },

  async createSectionTemplate(template: any) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const dbTemplate = {
      name: template.name,
      description: template.description || null,
      required: template.required || false,
      min_selections: template.minSelections || 0,
      max_selections: template.maxSelections || 1,
    }

    const { data, error } = await client
      .from('menu_section_templates')
      .insert([dbTemplate])
      .select()

    if (error) throw error
    return data[0]
  },

  async updateSectionTemplate(id: string, updates: any) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const dbUpdates: any = {}
    if (updates.name !== undefined) dbUpdates.name = updates.name
    if (updates.description !== undefined)
      dbUpdates.description = updates.description
    if (updates.required !== undefined) dbUpdates.required = updates.required
    if (updates.minSelections !== undefined)
      dbUpdates.min_selections = updates.minSelections
    if (updates.maxSelections !== undefined)
      dbUpdates.max_selections = updates.maxSelections

    const { data, error } = await client
      .from('menu_section_templates')
      .update(dbUpdates)
      .eq('id', id)
      .select()

    if (error) throw error
    return data[0]
  },

  async deleteSectionTemplate(id: string) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const { error } = await client
      .from('menu_section_templates')
      .delete()
      .eq('id', id)

    if (error) throw error
  },

  async createTemplateOption(option: any) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const dbOption = {
      template_id: option.templateId,
      name: option.name,
      price_modifier: option.priceModifier || 0,
      available: option.available !== false,
      display_order: option.displayOrder || 0,
    }

    const { data, error } = await client
      .from('menu_template_options')
      .insert([dbOption])
      .select()

    if (error) throw error
    return data[0]
  },

  async updateTemplateOption(id: string, updates: any) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const dbUpdates: any = {}
    if (updates.name !== undefined) dbUpdates.name = updates.name
    if (updates.priceModifier !== undefined)
      dbUpdates.price_modifier = updates.priceModifier
    if (updates.available !== undefined) dbUpdates.available = updates.available
    if (updates.displayOrder !== undefined)
      dbUpdates.display_order = updates.displayOrder

    const { data, error } = await client
      .from('menu_template_options')
      .update(dbUpdates)
      .eq('id', id)
      .select()

    if (error) throw error
    return data[0]
  },

  async deleteTemplateOption(id: string) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const { error } = await client
      .from('menu_template_options')
      .delete()
      .eq('id', id)

    if (error) throw error
  },

  async applyTemplateToItems(templateId: string, menuItemIds: string[]) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const { data, error } = await client.rpc('apply_template_to_items', {
      p_template_id: templateId,
      p_menu_item_ids: menuItemIds,
    })

    if (error) {
      // If RPC doesn't exist, do it manually
      if (error.code === '42883') {
        return this._applyTemplateManually(templateId, menuItemIds)
      }
      throw error
    }

    return data
  },

  async _applyTemplateManually(templateId: string, menuItemIds: string[]) {
    const templates = await this.getSectionTemplates()
    const template = templates.find((t: any) => t.id === templateId)
    if (!template) throw new Error('Plantilla no encontrada')

    let appliedCount = 0

    for (const itemId of menuItemIds) {
      // Check for existing section with same name
      const existingSections = await this.getMenuItemSections(itemId)
      if (existingSections.some((s: any) => s.name === template.name)) {
        continue // Skip duplicates
      }

      // Create section
      const newSection = await this.createMenuSection({
        menuItemId: itemId,
        name: template.name,
        description: template.description,
        required: template.required,
        minSelections: template.minSelections,
        maxSelections: template.maxSelections,
        displayOrder: existingSections.length,
      })

      // Copy options
      for (const opt of template.options) {
        await this.createSectionOption({
          sectionId: newSection.id,
          name: opt.name,
          priceModifier: opt.priceModifier,
          available: opt.available,
          displayOrder: opt.displayOrder,
        })
      }

      appliedCount++
    }

    return { success: true, applied_count: appliedCount }
  },

  // ============================================
  // PROMOTIONS
  // ============================================
  async getPromotions() {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const { data, error } = await client
      .from('promotions')
      .select('*')
      .order('created_at', { ascending: false })

    if (error) {
      throw error
    }

    // Transform snake_case to camelCase
    const promotions = (data || []).map((p: any) => ({
      id: p.id,
      name: p.name,
      description: p.description,
      type: p.type,
      value: parseFloat(p.value),
      code: p.code,
      minOrderAmount: parseFloat(p.min_order_amount || 0),
      maxDiscount: parseFloat(p.max_discount || 0),
      validFrom: p.valid_from,
      validTo: p.valid_to,
      active: p.active,
      usageLimit: p.usage_limit || 0,
      usageCount: p.usage_count || 0,
      applicableCompanies: p.applicable_companies || [],
      applicableMenuItems: p.applicable_menu_items || [],
      createdBy: p.created_by,
      createdAt: p.created_at,
    }))

    return promotions
  },

  async createPromotion(promotion: any) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const dbPromotion = {
      id: promotion.id || `promo-${Date.now()}`,
      name: promotion.name,
      description: promotion.description,
      type: promotion.type,
      value: promotion.value,
      code: promotion.code || null,
      min_order_amount: promotion.minOrderAmount || 0,
      max_discount: promotion.maxDiscount || 0,
      valid_from: promotion.validFrom,
      valid_to: promotion.validTo,
      active: promotion.active !== false,
      usage_limit: promotion.usageLimit || 0,
      usage_count: promotion.usageCount || 0,
      applicable_companies: promotion.applicableCompanies || [],
      applicable_menu_items: promotion.applicableMenuItems || [],
      created_by: promotion.createdBy || 'Admin',
    }

    const { data, error } = await client
      .from('promotions')
      .insert([dbPromotion])
      .select()

    if (error) {
      throw error
    }

    return data[0]
  },

  async updatePromotion(id: string, updates: any) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const dbUpdates: any = {}
    if (updates.name !== undefined) dbUpdates.name = updates.name
    if (updates.description !== undefined)
      dbUpdates.description = updates.description
    if (updates.type !== undefined) dbUpdates.type = updates.type
    if (updates.value !== undefined) dbUpdates.value = updates.value
    if (updates.code !== undefined) dbUpdates.code = updates.code
    if (updates.minOrderAmount !== undefined)
      dbUpdates.min_order_amount = updates.minOrderAmount
    if (updates.maxDiscount !== undefined)
      dbUpdates.max_discount = updates.maxDiscount
    if (updates.validFrom !== undefined)
      dbUpdates.valid_from = updates.validFrom
    if (updates.validTo !== undefined) dbUpdates.valid_to = updates.validTo
    if (updates.active !== undefined) dbUpdates.active = updates.active
    if (updates.usageLimit !== undefined)
      dbUpdates.usage_limit = updates.usageLimit
    if (updates.usageCount !== undefined)
      dbUpdates.usage_count = updates.usageCount

    const { data, error } = await client
      .from('promotions')
      .update(dbUpdates)
      .eq('id', id)
      .select()

    if (error) {
      throw error
    }

    return data[0]
  },

  async deletePromotion(id: string) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const { error } = await client.from('promotions').delete().eq('id', id)
    if (error) throw error
  },

  // ============================================
  // CLAIMS
  // ============================================
  async getClaims() {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const { data, error } = await client
      .from('claims')
      .select('*')
      .order('created_at', { ascending: false })

    if (error) {
      throw error
    }

    // Transform snake_case to camelCase
    const claims = (data || []).map((c: any) => ({
      id: c.id,
      orderId: c.order_id,
      orderNo: c.order_no,
      customerName: c.customer_name,
      company: c.company,
      branch: c.branch,
      type: c.type,
      status: c.status,
      priority: c.priority,
      description: c.description,
      resolution: c.resolution,
      createdAt: c.created_at,
      resolvedAt: c.resolved_at,
      createdBy: c.created_by,
      createdById: c.created_by_id,
    }))

    return claims
  },

  async createClaim(claim: any) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const dbClaim = {
      id: claim.id || `claim-${Date.now()}`,
      order_id: claim.orderId,
      order_no: claim.orderNo,
      customer_name: claim.customerName,
      company: claim.company,
      branch: claim.branch,
      type: claim.type,
      status: claim.status || 'Pendiente',
      priority: claim.priority || 'Media',
      description: claim.description,
      resolution: claim.resolution || null,
      created_by: claim.createdBy,
      created_by_id: claim.createdById,
    }

    const { data, error } = await client
      .from('claims')
      .insert([dbClaim])
      .select()

    if (error) {
      throw error
    }

    return data[0]
  },

  async updateClaim(id: string, updates: any) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const dbUpdates: any = {}
    if (updates.status !== undefined) dbUpdates.status = updates.status
    if (updates.resolution !== undefined)
      dbUpdates.resolution = updates.resolution
    if (updates.resolvedAt !== undefined)
      dbUpdates.resolved_at = updates.resolvedAt

    const { data, error } = await client
      .from('claims')
      .update(dbUpdates)
      .eq('id', id)
      .select()

    if (error) {
      throw error
    }

    return data[0]
  },

  // ============================================
  // ACTIVITY LOGS
  // ============================================
  async getActivityLogs() {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const { data, error } = await client
      .from('activity_logs')
      .select('*')
      .order('timestamp', { ascending: false })
      .limit(100)

    if (error) {
      throw error
    }

    // Transform to frontend format
    const logs = (data || []).map((log: any) => ({
      id: log.id?.toString() || generateSecureId('log'),
      timestamp: log.timestamp,
      user: log.user_name || 'Sistema',
      action: log.action,
      details: log.details,
      type: log.category,
    }))

    return logs
  },

  async createActivityLog(log: any) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const dbLog: any = {
      action: log.action,
      details: log.details,
      category: log.type || log.category,
    }

    // Include user_name if the column exists (graceful handling)
    if (log.user) {
      dbLog.user_name = log.user
    }

    const { data, error } = await client
      .from('activity_logs')
      .insert([dbLog])
      .select()

    if (error) {
      // If user_name column doesn't exist, retry without it
      if (error.message?.includes('user_name') || error.code === '42703') {
        const fallbackLog = {
          action: log.action,
          details: log.details,
          category: log.type || log.category,
        }
        const { data: fallbackData, error: fallbackError } = await client
          .from('activity_logs')
          .insert([fallbackLog])
          .select()
        if (fallbackError) throw fallbackError
        return fallbackData[0]
      }
      throw error
    }

    return data[0]
  },

  // ============================================
  // CHAT MESSAGES
  // ============================================
  async getChatMessages(conversationId?: string) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    let query = client
      .from('chat_messages')
      .select('*')
      .order('created_at', { ascending: true })

    if (conversationId) {
      query = query.eq('conversation_id', conversationId)
    }

    const { data, error } = await query

    if (error) {
      if (
        error.code === '42P01' ||
        error.message?.includes('does not exist') ||
        error.message?.includes('relation') ||
        error.code === 'PGRST204'
      ) {
        console.warn('[Supabase] chat_messages table not found.')
        return null
      }
      if (
        error.code === '42501' ||
        error.message?.includes('permission denied')
      ) {
        console.error('[Supabase] Permission denied on chat_messages.')
        throw new Error('Permiso denegado en chat_messages.')
      }
      throw error
    }

    return (data || []).map((msg: any) => ({
      id: msg.id,
      conversationId: msg.conversation_id,
      senderId: msg.sender_id,
      senderName: msg.sender_name,
      senderRole: msg.sender_role,
      senderCompany: msg.sender_company || '',
      text: msg.text,
      timestamp: msg.created_at,
      read: msg.read || false,
      fileUrl: msg.file_url || undefined,
      fileName: msg.file_name || undefined,
      fileType: msg.file_type || undefined,
      fileSize: msg.file_size || undefined,
    }))
  },

  async createChatMessage(message: any) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const dbMessage: any = {
      id: message.id,
      conversation_id: message.conversationId,
      sender_id: message.senderId,
      sender_name: message.senderName,
      sender_role: message.senderRole,
      sender_company: message.senderCompany || null,
      text: message.text,
      read: false,
    }

    // Add file fields if present
    if (message.fileUrl) dbMessage.file_url = message.fileUrl
    if (message.fileName) dbMessage.file_name = message.fileName
    if (message.fileType) dbMessage.file_type = message.fileType
    if (message.fileSize) dbMessage.file_size = message.fileSize

    const { data, error } = await client
      .from('chat_messages')
      .insert([dbMessage])
      .select()

    if (error) {
      if (error.code === '42P01' || error.message?.includes('does not exist')) {
        return null
      }
      console.error(
        '[Supabase] Error creating chat message:',
        error.code,
        error.message,
      )
      throw error
    }

    return data?.[0]
  },

  async deleteChatConversation(conversationId: string) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const { error } = await client
      .from('chat_messages')
      .delete()
      .eq('conversation_id', conversationId)

    if (error) {
      console.error('Error deleting conversation:', error)
      throw error
    }
  },

  // ── Chat conversation status (open/closed) ──
  async getChatClosedConversations(): Promise<
    Record<string, { closedAt: string; closedBy: string }>
  > {
    const client = getSupabase()
    if (!client) return {}

    try {
      const { data, error } = await client
        .from('settings')
        .select('value')
        .eq('key', 'chat_closed_conversations')
        .single()

      if (error) {
        if (error.code === 'PGRST116' || error.code === '42P01') return {}
        return {}
      }

      return data?.value || {}
    } catch {
      return {}
    }
  },

  async saveChatClosedConversations(
    closedMap: Record<string, { closedAt: string; closedBy: string }>,
  ) {
    const client = getSupabase()
    if (!client) return

    try {
      await client.from('settings').upsert(
        {
          key: 'chat_closed_conversations',
          value: closedMap,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'key' },
      )
    } catch (e) {
      console.error('[Chat] Error saving closed conversations to Supabase:', e)
    }
  },

  async uploadChatFile(
    file: File,
    conversationId: string,
  ): Promise<string | null> {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const fileExt = file.name.split('.').pop()
    const fileName = `${conversationId}/${Date.now()}-${Math.random().toString(36).substr(2, 8)}.${fileExt}`

    const { data, error } = await client.storage
      .from('chat-attachments')
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false,
      })

    if (error) {
      // If bucket doesn't exist, try creating it or log helpful message
      if (
        error.message?.includes('not found') ||
        error.message?.includes('Bucket')
      ) {
        console.error(
          '[Supabase] El bucket "chat-attachments" no existe. Créalo en Storage.',
        )
        console.error(
          '[Supabase] Ve a Supabase Dashboard → Storage → New Bucket → "chat-attachments" (público)',
        )
        throw new Error(
          'El almacenamiento de archivos no está configurado. Crea el bucket "chat-attachments" en Supabase Storage.',
        )
      }
      throw error
    }

    // Get public URL
    const { data: urlData } = client.storage
      .from('chat-attachments')
      .getPublicUrl(data.path)

    return urlData?.publicUrl || null
  },

  // ============================================
  // MENU IMAGE UPLOAD
  // ============================================
  async uploadMenuImage(file: File): Promise<string> {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const fileExt = file.name.split('.').pop()?.toLowerCase() || 'jpg'
    const fileName = `${Date.now()}-${Math.random().toString(36).substr(2, 8)}.${fileExt}`

    const { data, error } = await client.storage
      .from('menu-images')
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false,
        contentType: file.type,
      })

    if (error) {
      if (
        error.message?.includes('not found') ||
        error.message?.includes('Bucket')
      ) {
        throw new Error(
          'El bucket "menu-images" no existe. Ve a Supabase Dashboard → Storage → New Bucket → "menu-images" (público).',
        )
      }
      throw error
    }

    const { data: urlData } = client.storage
      .from('menu-images')
      .getPublicUrl(data.path)

    return urlData?.publicUrl || ''
  },

  // ============================================
  // REAL-TIME SUBSCRIPTIONS
  // ============================================
  subscribeToOrders(callback: (payload: any) => void) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    return client
      .channel('orders-channel')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'orders' },
        (payload) => {
          callback(payload)
        },
      )
      .subscribe()
  },

  subscribeToEmployees(callback: (payload: any) => void) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    return client
      .channel('employees-channel')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'employees' },
        callback,
      )
      .subscribe()
  },

  subscribeToClaims(callback: (payload: any) => void) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    return client
      .channel('claims-channel')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'claims' },
        callback,
      )
      .subscribe()
  },

  subscribeToChatMessages(callback: (payload: any) => void) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    return client
      .channel('chat-messages-channel')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'chat_messages' },
        callback,
      )
      .subscribe()
  },

  async markChatMessagesAsRead(conversationId: string, readerRole: string) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const senderRole = readerRole === 'admin' ? 'employee' : 'admin'

    const { error } = await client
      .from('chat_messages')
      .update({ read: true })
      .eq('conversation_id', conversationId)
      .eq('sender_role', senderRole)
      .eq('read', false)

    if (error) {
      console.error('[Supabase] Error marking messages as read:', error)
      throw error
    }
  },

  subscribeToCompanies(callback: (payload: any) => void) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    return client
      .channel('companies-channel')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'companies' },
        callback,
      )
      .subscribe()
  },

  // ============================================
  // ADMIN USERS (via secure RPC functions)
  // ============================================

  async getAdminUsers() {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const { data, error } = await client.rpc('list_admin_users')
    if (error) throw new Error('Error al cargar usuarios: ' + error.message)
    return data || []
  },

  async createAdminUser(user: {
    username: string
    password: string
    name: string
    email: string
    role?: string
    permissions?: any
  }) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const { data, error } = await client.rpc('create_admin_user', {
      p_username: user.username,
      p_password: user.password,
      p_name: user.name,
      p_email: user.email,
      p_role: user.role || 'admin',
      p_permissions: user.permissions || {},
    })

    if (error) throw new Error(error.message)
    return data
  },

  async updateAdminUser(
    id: string,
    updates: {
      name?: string
      email?: string
      password?: string
      role?: string
      status?: string
      permissions?: any
    },
  ) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const { data, error } = await client.rpc('update_admin_user', {
      p_id: id,
      p_name: updates.name || null,
      p_email: updates.email || null,
      p_password: updates.password || null,
      p_role: updates.role || null,
      p_status: updates.status || null,
      p_permissions: updates.permissions || null,
    })

    if (error) throw new Error(error.message)
    return data
  },

  async deleteAdminUser(id: string) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const { data, error } = await client.rpc('delete_admin_user', {
      p_id: id,
    })

    if (error) throw new Error(error.message)
    return data
  },

  // ============================================
  // SETTINGS
  // ============================================
  async getSettings(key: string = 'general') {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const { data, error } = await client
      .from('settings')
      .select('*')
      .eq('key', key)
      .single()

    if (error) {
      // Table might not exist or no row found - not a critical error
      if (error.code === 'PGRST116' || error.code === '42P01') {
        return null
      }
      throw error
    }

    return data?.value || null
  },

  async saveSettings(key: string = 'general', value: any) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const { data, error } = await client
      .from('settings')
      .upsert(
        { key, value, updated_at: new Date().toISOString() },
        { onConflict: 'key' },
      )
      .select()

    if (error) {
      throw error
    }

    return data?.[0]
  },

  // ============================================
  // EMPLOYEE BALANCES (SISTEMA DE SUBSIDIOS)
  // ============================================

  async getEmployeeBalance(employeeId: string, month: number, year: number) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const { data, error } = await client
      .from('employee_balances')
      .select('*')
      .eq('employee_id', employeeId)
      .eq('month', month)
      .eq('year', year)
      .single()

    if (error) {
      if (error.code === 'PGRST116') return null // Not found
      if (error.code === '42P01') return null // Table doesn't exist yet
      throw error
    }

    return data
      ? {
          id: data.id,
          employeeId: data.employee_id,
          companyId: data.company_id,
          month: data.month,
          year: data.year,
          assignedAmount: parseFloat(data.assigned_amount),
          currentBalance: parseFloat(data.current_balance),
          assignedBy: data.assigned_by,
          assignedAt: data.assigned_at,
          modifiedBy: data.modified_by,
          modifiedAt: data.modified_at,
          notes: data.notes,
        }
      : null
  },

  async getEmployeeBalances(employeeId: string) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const { data, error } = await client
      .from('employee_balances')
      .select('*')
      .eq('employee_id', employeeId)
      .order('year', { ascending: false })
      .order('month', { ascending: false })

    if (error) {
      if (error.code === '42P01') return []
      throw error
    }

    return (data || []).map((d: any) => ({
      id: d.id,
      employeeId: d.employee_id,
      companyId: d.company_id,
      month: d.month,
      year: d.year,
      assignedAmount: parseFloat(d.assigned_amount),
      currentBalance: parseFloat(d.current_balance),
      assignedBy: d.assigned_by,
      assignedAt: d.assigned_at,
      modifiedBy: d.modified_by,
      modifiedAt: d.modified_at,
      notes: d.notes,
    }))
  },

  async getCompanyBalances(companyId: string, month?: number, year?: number) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    let query = client
      .from('employee_balances')
      .select('*')
      .eq('company_id', companyId)

    if (month !== undefined) query = query.eq('month', month)
    if (year !== undefined) query = query.eq('year', year)

    const { data, error } = await query
      .order('year', { ascending: false })
      .order('month', { ascending: false })

    if (error) {
      if (error.code === '42P01') return []
      throw error
    }

    return (data || []).map((d: any) => ({
      id: d.id,
      employeeId: d.employee_id,
      companyId: d.company_id,
      month: d.month,
      year: d.year,
      assignedAmount: parseFloat(d.assigned_amount),
      currentBalance: parseFloat(d.current_balance),
      assignedBy: d.assigned_by,
      assignedAt: d.assigned_at,
      modifiedBy: d.modified_by,
      modifiedAt: d.modified_at,
      notes: d.notes,
    }))
  },

  async getAllBalances(month?: number, year?: number) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    let query = client.from('employee_balances').select('*')
    if (month !== undefined) query = query.eq('month', month)
    if (year !== undefined) query = query.eq('year', year)

    const { data, error } = await query
      .order('year', { ascending: false })
      .order('month', { ascending: false })

    if (error) {
      if (error.code === '42P01') return []
      throw error
    }

    return (data || []).map((d: any) => ({
      id: d.id,
      employeeId: d.employee_id,
      companyId: d.company_id,
      month: d.month,
      year: d.year,
      assignedAmount: parseFloat(d.assigned_amount),
      currentBalance: parseFloat(d.current_balance),
      assignedBy: d.assigned_by,
      assignedAt: d.assigned_at,
      modifiedBy: d.modified_by,
      modifiedAt: d.modified_at,
      notes: d.notes,
    }))
  },

  async assignBalance(params: {
    employeeId: string
    companyId: string
    month: number
    year: number
    amount: number
    assignedBy: string
    notes?: string
  }) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    // Check for existing balance (prevent duplicates)
    const existing = await this.getEmployeeBalance(
      params.employeeId,
      params.month,
      params.year,
    )
    if (existing) {
      throw new Error(
        `Ya existe un balance asignado para este empleado en ${params.month}/${params.year}. Solo un ADMIN puede modificarlo.`,
      )
    }

    const { data, error } = await client
      .from('employee_balances')
      .insert([
        {
          employee_id: params.employeeId,
          company_id: params.companyId,
          month: params.month,
          year: params.year,
          assigned_amount: params.amount,
          current_balance: params.amount,
          assigned_by: params.assignedBy,
          notes: params.notes || null,
        },
      ])
      .select()

    if (error) throw error
    return data[0]
  },

  async modifyBalance(params: {
    balanceId: string
    newAmount: number
    currentBalance: number
    modifiedBy: string
    notes?: string
  }) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const { data, error } = await client
      .from('employee_balances')
      .update({
        assigned_amount: params.newAmount,
        current_balance: params.currentBalance,
        modified_by: params.modifiedBy,
        modified_at: new Date().toISOString(),
        notes: params.notes || null,
      })
      .eq('id', params.balanceId)
      .select()

    if (error) throw error
    return data[0]
  },

  async consumeBalance(
    employeeId: string,
    month: number,
    year: number,
    amount: number,
  ) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    // Use atomic RPC function to prevent race conditions
    const { data, error } = await client.rpc('consume_employee_balance', {
      p_employee_id: employeeId,
      p_month: month,
      p_year: year,
      p_amount: amount,
    })

    if (error) {
      // If RPC doesn't exist yet, fall back to manual update
      if (error.code === '42883') {
        const balance = await this.getEmployeeBalance(employeeId, month, year)
        if (!balance) throw new Error('Balance no encontrado')
        if (balance.currentBalance < amount)
          throw new Error('Balance insuficiente')

        const { error: updateError } = await client
          .from('employee_balances')
          .update({ current_balance: balance.currentBalance - amount })
          .eq('employee_id', employeeId)
          .eq('month', month)
          .eq('year', year)

        if (updateError) throw updateError
        return { success: true, newBalance: balance.currentBalance - amount }
      }
      throw error
    }

    if (!data?.success) {
      throw new Error(data?.error || 'Error al consumir balance')
    }

    return data
  },

  /**
   * Refund balance back to an employee (e.g., after a claim is accepted).
   * Increases current_balance up to the assigned_amount cap.
   */
  async refundBalance(
    employeeId: string,
    month: number,
    year: number,
    amount: number,
    performedBy: string = 'Admin',
    notes?: string,
  ) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const balance = await this.getEmployeeBalance(employeeId, month, year)
    if (!balance) throw new Error('Balance no encontrado para este empleado')

    const newBalance = Math.min(
      balance.assignedAmount,
      balance.currentBalance + amount,
    )

    const { error } = await client
      .from('employee_balances')
      .update({
        current_balance: newBalance,
        modified_by: performedBy,
        modified_at: new Date().toISOString(),
      })
      .eq('employee_id', employeeId)
      .eq('month', month)
      .eq('year', year)

    if (error) throw error

    // Log the refund
    await this.createBalanceLog({
      employeeId,
      actionType: 'balance_refunded',
      amount,
      previousAmount: balance.currentBalance,
      month,
      year,
      performedBy,
      performedByRole: 'admin',
      notes: notes || 'Reembolso por reclamación',
    }).catch(() => {}) // Non-critical

    return { success: true, newBalance }
  },

  // ============================================
  // BALANCE LOGS (AUDITORÍA)
  // ============================================

  async createBalanceLog(log: {
    companyId?: string
    companyName?: string
    employeeId?: string
    employeeName?: string
    actionType: string
    amount?: number
    previousAmount?: number
    month?: number
    year?: number
    performedBy: string
    performedByRole?: string
    notes?: string
  }) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const { data, error } = await client
      .from('balance_logs')
      .insert([
        {
          company_id: log.companyId || null,
          company_name: log.companyName || null,
          employee_id: log.employeeId || null,
          employee_name: log.employeeName || null,
          action_type: log.actionType,
          amount: log.amount ?? null,
          previous_amount: log.previousAmount ?? null,
          month: log.month ?? null,
          year: log.year ?? null,
          performed_by: log.performedBy,
          performed_by_role: log.performedByRole || 'admin',
          notes: log.notes || null,
        },
      ])
      .select()

    if (error) {
      if (error.code === '42P01') return null // Table doesn't exist yet
      throw error
    }

    return data[0]
  },

  async getBalanceLogs(filters?: {
    companyId?: string
    employeeId?: string
    limit?: number
  }) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    let query = client
      .from('balance_logs')
      .select('*')
      .order('created_at', { ascending: false })

    if (filters?.companyId) query = query.eq('company_id', filters.companyId)
    if (filters?.employeeId) query = query.eq('employee_id', filters.employeeId)
    if (filters?.limit) query = query.limit(filters.limit)

    const { data, error } = await query

    if (error) {
      if (error.code === '42P01') return []
      throw error
    }

    return (data || []).map((d: any) => ({
      id: d.id,
      companyId: d.company_id,
      companyName: d.company_name,
      employeeId: d.employee_id,
      employeeName: d.employee_name,
      actionType: d.action_type,
      amount: d.amount !== null ? parseFloat(d.amount) : null,
      previousAmount:
        d.previous_amount !== null ? parseFloat(d.previous_amount) : null,
      month: d.month,
      year: d.year,
      performedBy: d.performed_by,
      performedByRole: d.performed_by_role,
      notes: d.notes,
      createdAt: d.created_at,
    }))
  },

  // ============================================
  // REAL-TIME SUBSCRIPTIONS (BALANCES)
  // ============================================
  subscribeToBalances(callback: (payload: any) => void) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    return client
      .channel('employee-balances-channel')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'employee_balances' },
        callback,
      )
      .subscribe()
  },

  // ============================================
  // DEMO REQUESTS
  // ============================================
  async getDemoRequests() {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const { data, error } = await client
      .from('demo_requests')
      .select('*')
      .order('created_at', { ascending: false })

    if (error) {
      if (error.code === '42P01' || error.message?.includes('does not exist')) {
        console.warn(
          '[Supabase] demo_requests table not found. Run the SQL migration.',
        )
        return null
      }
      throw error
    }

    return (data || []).map((r: any) => ({
      id: r.id,
      name: r.name,
      email: r.email,
      company: r.company,
      phone: r.phone,
      employees: r.employees,
      message: r.message || '',
      status: r.status || 'nueva',
      notes: r.notes || '',
      createdAt: r.created_at,
    }))
  },

  async createDemoRequest(request: any) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const dbRequest = {
      name: request.name,
      email: request.email,
      company: request.company,
      phone: request.phone,
      employees: request.employees,
      message: request.message || '',
      status: 'nueva',
      notes: '',
    }

    const { data, error } = await client
      .from('demo_requests')
      .insert([dbRequest])
      .select()

    if (error) {
      if (error.code === '42P01' || error.message?.includes('does not exist')) {
        console.warn('[Supabase] demo_requests table not found.')
        return null
      }
      throw error
    }

    return data?.[0]
  },

  async updateDemoRequest(id: string, updates: any) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const dbUpdates: any = {}
    if (updates.status !== undefined) dbUpdates.status = updates.status
    if (updates.notes !== undefined) dbUpdates.notes = updates.notes

    const { data, error } = await client
      .from('demo_requests')
      .update(dbUpdates)
      .eq('id', id)
      .select()

    if (error) throw error
    return data?.[0]
  },

  async deleteDemoRequest(id: string) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const { error } = await client.from('demo_requests').delete().eq('id', id)
    if (error) throw error
  },

  async getNewDemoRequestsCount() {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const { count, error } = await client
      .from('demo_requests')
      .select('*', { count: 'exact', head: true })
      .eq('status', 'nueva')

    if (error) {
      if (error.code === '42P01' || error.message?.includes('does not exist')) {
        return 0
      }
      throw error
    }

    return count || 0
  },

  subscribeToDemoRequests(callback: (payload: any) => void) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    return client
      .channel('demo-requests-channel')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'demo_requests' },
        callback,
      )
      .subscribe()
  },
}

// Export secure ID generator for use elsewhere
export { generateSecureId }

// Export types
export type Order = {
  id: string
  order_no: string
  order_date: string
  delivery_date: string
  type: string
  employee_name: string
  employee_id: string
  company: string
  branch: string
  product: string
  quantity: number
  catalog_type: string
  subtotal: string
  discount: string
  subsidy_amount: string
  subsidy_applied_by: string
  total: string
  status: 'Pendiente' | 'En Preparación' | 'Listo' | 'Entregado' | 'Cancelado'
  label_status: 'Pendiente' | 'Generado'
  tax_exempt: boolean
  created_at: string
}

export type Employee = {
  id: string
  name: string
  email: string
  username: string
  // password excluded - never sent to frontend
  position: string
  company_id: number
  branch_id: string
  status: string
  permissions: any
  created_at: string
}

export type Claim = {
  id: string
  order_id: string
  order_no: string
  customer_name: string
  company: string
  branch: string
  type: string
  status: string
  priority: string
  description: string
  resolution: string | null
  created_at: string
  resolved_at: string | null
  created_by: string
  created_by_id: string
}

export type ActivityLog = {
  id: number
  action: string
  details: string
  category: string
  timestamp: string
}

export type Company = {
  id: number
  name: string
  rnc: string
  logo: string | null
  branches: any[]
  employees: number
  tax_exempt: boolean
  subsidy: string
  subsidyEnabled: boolean
  order_cutoff_time: string
  delivery_start_time: string
  delivery_end_time: string
  contact_name: string | null
  contact_email: string | null
  contact_phone: string | null
  company_prices: any
  created_at: string
}

export type MenuItem = {
  id: string
  name: string
  description: string
  category: string
  price: number
  image: string | null
  catalog_type: string
  available: boolean
  created_at: string
}

export type Promotion = {
  id: string
  name: string
  description: string
  type: string
  value: number
  code: string | null
  min_order_amount: number
  max_discount: number
  valid_from: string
  valid_to: string
  active: boolean
  usage_limit: number
  usage_count: number
  applicable_companies: any[]
  applicable_menu_items: any[]
  created_by: string
  created_at: string
}
